import { getDateKey } from "./index";

// Public holidays per contract country, used to know which days are not expected to be worked.

export const HOLIDAY_COUNTRIES = [
  { value: "NONE", label: "Aucun" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Espagne (Barcelone)" },
  { value: "AL", label: "Albanie" },
];

// Eid dates are set each year by decree in Albania. Add the next year once it is announced.
const ALBANIAN_EID = {
  2025: { fitr: "2025-03-30", adha: "2025-06-06" },
  2026: { fitr: "2026-03-20", adha: "2026-05-27" },
  2027: { fitr: "2027-03-10", adha: "2027-05-17" }, // estimated
};

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isWeekend(date) {
  return date.getDay() === 0 || date.getDay() === 6;
}

function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// Julian computation shifted to the Gregorian calendar (valid 1900–2099).
function getOrthodoxEasterSunday(year) {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  return new Date(year, month - 1, day + 13);
}

function getFrenchHolidays(year) {
  const easter = getEasterSunday(year);
  return {
    [`${year}-01-01`]: "Jour de l'An",
    [getDateKey(addDays(easter, 1))]: "Lundi de Pâques",
    [`${year}-05-01`]: "Fête du Travail",
    [`${year}-05-08`]: "Victoire 1945",
    [getDateKey(addDays(easter, 39))]: "Ascension",
    [getDateKey(addDays(easter, 50))]: "Lundi de Pentecôte",
    [`${year}-07-14`]: "Fête nationale",
    [`${year}-08-15`]: "Assomption",
    [`${year}-11-01`]: "Toussaint",
    [`${year}-11-11`]: "Armistice 1918",
    [`${year}-12-25`]: "Noël",
  };
}

// National holidays + Catalonia (the Spanish team works from Barcelona). Municipal holidays are not included.
function getSpanishHolidays(year) {
  const easter = getEasterSunday(year);
  return {
    [`${year}-01-01`]: "Año Nuevo",
    [`${year}-01-06`]: "Epifanía",
    [getDateKey(addDays(easter, -2))]: "Viernes Santo",
    [getDateKey(addDays(easter, 1))]: "Lunes de Pascua",
    [`${year}-05-01`]: "Fiesta del Trabajo",
    [`${year}-06-24`]: "Sant Joan",
    [`${year}-08-15`]: "Asunción",
    [`${year}-09-11`]: "Diada de Catalunya",
    [`${year}-10-12`]: "Fiesta Nacional",
    [`${year}-11-01`]: "Todos los Santos",
    [`${year}-12-06`]: "Día de la Constitución",
    [`${year}-12-08`]: "Inmaculada Concepción",
    [`${year}-12-25`]: "Navidad",
    [`${year}-12-26`]: "Sant Esteve",
  };
}

// In Albania a holiday falling on a weekend is given back on the next working day.
function getAlbanianHolidays(year) {
  const eid = ALBANIAN_EID[year] || {};
  const list = [
    [`${year}-01-01`, "Viti i Ri"],
    [`${year}-01-02`, "Viti i Ri"],
    [`${year}-03-14`, "Dita e Verës"],
    [`${year}-03-22`, "Dita e Nevruzit"],
    [getDateKey(getEasterSunday(year)), "Pashkët Katolike"],
    [getDateKey(getOrthodoxEasterSunday(year)), "Pashkët Ortodokse"],
    [`${year}-05-01`, "Dita e Punëtorëve"],
    [eid.fitr, "Bajrami i Madh"],
    [eid.adha, "Kurban Bajrami"],
    [`${year}-09-05`, "Dita e Nënë Terezës"],
    [`${year}-11-28`, "Dita e Pavarësisë"],
    [`${year}-11-29`, "Dita e Çlirimit"],
    [`${year}-12-08`, "Dita Kombëtare e Rinisë"],
    [`${year}-12-25`, "Krishtlindjet"],
  ].filter(([key]) => key);

  const holidays = {};
  list.forEach(([key, name]) => {
    if (!holidays[key]) holidays[key] = name;
  });
  const shifted = new Set();
  list.forEach(([key, name]) => {
    if (shifted.has(key)) return; // both Easters can fall on the same Sunday
    shifted.add(key);
    const [y, m, d] = key.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    if (!isWeekend(date)) return;
    let observed = addDays(date, 1);
    while (isWeekend(observed) || holidays[getDateKey(observed)]) observed = addDays(observed, 1);
    holidays[getDateKey(observed)] = `${name} (récupéré)`;
  });
  return holidays;
}

const PROVIDERS = { FR: getFrenchHolidays, ES: getSpanishHolidays, AL: getAlbanianHolidays };

// Default country from the user's contract, then from the office for freelancers.
export function getContractHolidayCountry(user) {
  if (!user) return "NONE";
  if (["Tirana", "Konstrukt Shpk"].includes(user.organisation_name) || user.contract === "Me kohe te plote") return "AL";
  if (user.organisation_name === "Selegoloco" || user.company_country === "ES" || user.division === "SELESPAGNE") return "ES";
  if (user.organisation_name === "Le Collectif" || user.company_country === "FR" || user.division === "LE_COLLECTIF") return "FR";
  if (user.office === "TIRANA") return "AL";
  if (user.office === "BARCELONE") return "ES";
  if (user.office === "PARIS") return "FR";
  return "NONE";
}

export function getHolidayName(date, country) {
  const provider = PROVIDERS[country];
  if (!provider) return "";
  return provider(new Date(date).getFullYear())[getDateKey(date)] || "";
}
